import { Button } from "@/components/ui/button";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import useTableData from "@/components/ui/table/data-table-logic";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { displayDate, displayValue, formatToPKR } from "@/lib/helper";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

import InvoiceForm from "../modal/form";
import { useGetInvoicesQuery } from "@/services/invoiceApi";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { ErrorType } from "@/types";
import { InvoiceDetails } from "@/types/invoice";
import { MemberTableDatatypes } from "@/types/member";
import TableFilters from "@/components/ui/table/data-table-filter";
import { invoiceStatus, invoiceType } from "@/lib/constants/global";
import { DataTableRowActions } from "./row-action-column";
import { useToast } from "@/components/ui/use-toast";
import { InvoiceStatus, InvoiceType, POSProductType } from "@/types/enums";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: ''
};
const InvoiceTable = () => {
  const finance = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).finance ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [openFilter, setOpenFilter] = React.useState(false);
  const [action, setAction] = React.useState<"add" | "edit" | "member-created">("add");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [filterData, setFilterData] = React.useState({});
  const [editInvoiceData, setEditInvoiceData] = React.useState<
    InvoiceDetails | undefined
  >(undefined);
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  // Get organization ID from Redux store
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Fetch member data for the combobox
  const { data: memberList } = useGetAllMemberQuery({
    org_id: orgId,
    query: "sort_key=id&sort_order=desc",
  });

  const {
    data: invoiceData,
    isLoading: isInvoiceDataLoading,
    refetch,
    error,
    isError,
  } = useGetInvoicesQuery(
    {
      query: query,
      org_id: orgId,
    },
    {
      skip: query == "",
    }
  );

  const tableData = React.useMemo(() => {
    return Array.isArray(invoiceData?.data) ? invoiceData?.data : [];
  }, [invoiceData?.data]);

  const memberListData = React.useMemo(() => {
    return Array.isArray(memberList?.data) ? memberList?.data : [];
  }, [memberList]);

  const handleOpenForm = (payload: InvoiceDetails) => {
    if (payload.items.some((item) => item.item_type == POSProductType.Facilities)) {
      return;
    }
    setAction("edit");
    setEditInvoiceData(payload);
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<InvoiceDetails> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={finance}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
      />
    ),
  };
  const columns: ColumnDef<InvoiceDetails>[] = [
    {
      accessorKey: "invoiceid",
      meta: "invoiceid",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Invoice Number"
          sortKey="id"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.invoice_number)}
          </div>
        );
      },
    },

    {
      accessorKey: "date",
      meta: "date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Invoice Date"
          sortKey="created_at"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayDate(row.original.created_at)}
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      meta: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex capitalize items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.member_name)}
          </div>
        );
      },
    },
    {
      accessorKey: "quantity",
      meta: "quantity",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Quantity"
          sortKey="quantity"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.total_quantity)}
          </div>
        );
      },
    },
    {
      accessorKey: "type",
      meta: "type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Type"
          sortKey="invoice_type"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.invoice_type)}
          </div>
        );
      },
    },

    {
      accessorKey: "Tax",
      meta: "tax_amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Tax Amount"
          sortKey="tax_amount"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {formatToPKR(row?.original.tax_amount)}
          </div>
        );
      },
    },
    {
      accessorKey: "TotalAmount",
      meta: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Amount"
          sortKey="total"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {formatToPKR(row?.original.total)}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Invoice Status"
          sortKey="status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.status)}
          </div>
        );
      },
    },
    ...(finance !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: tableData as InvoiceDetails[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = invoiceData?.filtered_counts ?? 0;

  React.useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  function handleCreateInvoice() {
    setAction("add");
    setIsDialogOpen(true);
  }
  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "type",
      label: "Invoice Type",
      options: invoiceType.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("type", value),
    },
    {
      type: "select",
      name: "status",
      label: "Invoice Status",
      options: invoiceStatus.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "combobox",
      name: "member_id",
      label: "Member",
      options: memberListData.map((member: MemberTableDatatypes) => ({
        value: member.id.toString(),
        label: `${member.own_member_id ? `${member.own_member_id} • ` : ''}${member.first_name} ${member.last_name}`,
      })),
      function: (value: string) => handleFilterChange("member_id", value),
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 px-3">
        {/* Table Filter component */}
        <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

        {/* Buttons Container */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            access={finance !== "read"}
            className="bg-primary text-sm text-black flex items-center gap-1 h-8 px-2"
            onClick={handleCreateInvoice}
          >
            <i className="fa-solid fa-plus"></i>
            <span className="hidden sm:inline">Create New</span>
          </Button>
          {/* <DataTableViewOptions table={table} /> */}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3 mt-4">
        {isInvoiceDataLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : tableData.length > 0 ? (
          tableData.map((row: InvoiceDetails) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm space-y-2"
              onClick={() => {
                if (
                  finance !== "read" &&
                  row.status !== InvoiceStatus.Paid &&
                  row.status !== InvoiceStatus.Refunded &&
                  row.status !== InvoiceStatus.Cancelled
                ) {
                  handleOpenForm(row);
                }
              }}
            >
              {/* Header row: invoice number + actions */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-base">{row.invoice_number || "—"}</p>
                  <p className="text-xs text-gray-400 capitalize">{row.member_name || "—"}</p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DataTableRowActions
                    access={finance}
                    data={row}
                    refetch={refetch}
                    handleEdit={handleOpenForm}
                  />
                </div>
              </div>

              {/* Details */}
              <div className="flex flex-col gap-1 pt-2 border-t text-sm text-gray-500">
                <div className="flex justify-between">
                  <span><strong>Date:</strong> {displayDate(row.created_at)}</span>
                  <span>
                    <strong>Type:</strong>{" "}
                    <span className="capitalize">{row.invoice_type || "—"}</span>
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>
                    <strong>Status:</strong>{" "}
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                        row.status === InvoiceStatus.Paid
                          ? "bg-green-100 text-green-700"
                          : row.status === InvoiceStatus.Cancelled
                          ? "bg-red-100 text-red-700"
                          : row.status === InvoiceStatus.Refunded
                          ? "bg-purple-100 text-purple-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {row.status || "—"}
                    </span>
                  </span>
                  <span className="font-semibold text-black">{formatToPKR(row.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span><strong>Tax:</strong> {formatToPKR(row.tax_amount)}</span>
                  <span><strong>Qty:</strong> {row.total_quantity ?? "—"}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No invoices found.
          </div>
        )}
      </div>

      {/* Data Table — desktop only */}
      <div className="hidden md:block mt-4 bg-white">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          ></ScrollBar>
          {/* <Table className="w-full overflow-x-scroll "> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isInvoiceDataLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (finance !== "read" && (
                        row.original.status !== InvoiceStatus.Paid &&
                        row.original.status !== InvoiceStatus.Refunded &&
                        row.original.status !== InvoiceStatus.Cancelled &&
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleOpenForm(row.original)
                      }
                    }}
                    className={finance !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : tableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No invoice added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No invoice found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <InvoiceForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        refetch={refetch}
        editModeData={editInvoiceData}

      />

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </>
  );
};

export default InvoiceTable;
